import { useCallback, useRef } from "react";

interface useIframeOptions {
  iframeId: string;
}
export default function useIframe({ iframeId }: useIframeOptions) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const setupFrame = useCallback(() => {
    let el = null;

    if (iframeId) {
      el = document.getElementById(iframeId);
    }

    if (!el) {
      el = document.createElement('iframe')
      el.style.opacity = '0';
      el.id = iframeId
      document.body.appendChild(el)
    }

    if (!(el instanceof HTMLIFrameElement))
      throw new Error("[Sonaris] provided id `{id}` does not belong to an Iframe".replace('{id}', iframeId))

    // NOTE: This will prevent the iframe navigation from reloading the parent
    el.addEventListener('load', (e) => e.preventDefault())
    iframeRef.current = el as HTMLIFrameElement
  }, [iframeId])

  const disposeFrame = useCallback(({ load_url_in_parent = false }: { load_url_in_parent: boolean }) => {
    const iframeUrl = iframeRef.current?.contentWindow?.location.href
    if (iframeRef.current) {
      iframeRef.current.remove()
      iframeRef.current = null;
    }

    if (iframeUrl && load_url_in_parent) {
      window.location.assign(iframeUrl)
    }
  }, [])

  const isDocumentContentReady = useCallback(() => {
    const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document
    const href = iframeRef.current?.contentWindow?.location.href

    // NOTE: the document exists, and has a valid url and is loaded fully (in the dom)
    return !!doc && !!href && doc.readyState === 'complete'
  }, [])

  const navigate = useCallback((url: string) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.location.assign(url)
    }
  }, [])

  const clickElement = useCallback((target: string) => {
    const el = iframeRef.current?.contentWindow?.document.querySelector(target)
    if (el instanceof HTMLElement) {
      el.focus()
      el.scrollIntoView({ behavior: 'smooth' })
      el.click()
    }
  }, [])

  const scrollElementIntoView = useCallback((target: string) => {
    const el = iframeRef.current?.contentWindow?.document.querySelector(target)
    if (el instanceof HTMLElement) {
      el.focus()
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [])

  return { iframeRef, setupFrame, disposeFrame, navigate, clickElement, scrollElementIntoView, isDocumentContentReady }
}
